import {
  useController,
  Control,
  FieldValues,
  RegisterOptions,
  Path,
} from "react-hook-form";

interface InputProps<T extends FieldValues> {
  name: Path<T>;
  control?: Control<T>;
  label?: string;
  placeholder?: string;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "setValueAs" | "disabled" | "valueAsNumber" | "valueAsDate"
  >;
  type?: "text" | "number" | "email" | "password" | "checkbox";
  disabled?: boolean;
  defaultValue?: any;
  autoComplete?: string;
  allowOnlyLetters?: boolean;
  maxLength?: number;
}

const Input = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  type = "text",
  disabled = false,
  defaultValue = "",
  autoComplete = "off",
  allowOnlyLetters = false,
  maxLength,
}: InputProps<T>) => {
  const {
    field: { onChange, onBlur, value = defaultValue, ref },
    fieldState: { error },
  } = useController<T>({
    name,
    control,
    rules,
    defaultValue,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    if (type === "number") {
      inputValue = inputValue === "" ? "" : inputValue.replace(/[^0-9]/g, "");
      if (maxLength && inputValue.length > maxLength) return;
      onChange(inputValue);
    } else if (allowOnlyLetters) {
      const sanitizedValue = inputValue.replace(/[^A-Za-z\s'-]/g, "");
      onChange(sanitizedValue);
    } else {
      onChange(inputValue);
    }
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {rules?.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        type={type}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
          error ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
        } ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}`}
        value={(value as string | number) || ""}
        onChange={handleChange}
        onBlur={onBlur}
        ref={ref}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        maxLength={maxLength}
      />
      {error && (
        <div className="text-red-500 text-sm mt-1">{error.message}</div>
      )}
    </div>
  );
};

export default Input;
